import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Camera, ChevronRight, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { EditableTagsReadOnly } from '@/components/EditableTagsReadOnly'
import { SortDropdown } from '@/components/SortDropdown'
import { VinChip } from '@/components/VinChip'
import { VehicleDialog } from '@/components/vehicles/VehicleDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { track } from '@/lib/analytics'
import { phoneMatches } from '@/lib/phone'
import type { Tag } from '@/lib/tags'
import { useListSort } from '@/lib/useListSort'

type VehicleListRow = {
  id: string
  year: number | null
  make: string | null
  model: string | null
  trim: string | null
  engine: string | null
  drivetrain: string | null
  body: string | null
  vin: string | null
  plate: string | null
  mileage: number | null
  color: string | null
  customer_id: string | null
  owner_name: string | null
  owner_phone: string | null
  tag_chips: Tag[]
}

function vehicleLabel(v: VehicleListRow): string {
  return [v.year, v.make, v.model].filter(Boolean).join(' ') || '—'
}

function engineLabel(v: VehicleListRow): string {
  const parts = [v.engine, v.drivetrain].filter(Boolean) as string[]
  return parts.join(' · ') || '—'
}

function colorBodyLabel(v: VehicleListRow): string {
  const parts = [v.color, v.body].filter(Boolean) as string[]
  return parts.join(' · ')
}

function formatMileage(km: number | null): string {
  if (km === null || km === undefined) return '—'
  return km.toLocaleString()
}

// Visual flag for vehicles with no `customer_id` — amber exclamation
// + native tooltip on hover/long-press. Surfaces in both the mobile
// card and the desktop table; explicit affordance for the "Emil
// added a car before he knew the customer" case so it doesn't fade
// into the rest of the un-owned rows when triaging the list.
// Amber = waiting-on-you (DESIGN.md §2): the owner link is a gap only
// the mechanic can fill.
function OrphanOwnerBadge({ tooltip }: { tooltip: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-attention-text"
      title={tooltip}
    >
      <AlertTriangle className="size-3.5" aria-hidden="true" />
      <span className="sr-only">{tooltip}</span>
      <span aria-hidden="true">—</span>
    </span>
  )
}

// Visual flag for vehicles missing a VIN — ink-2 camera icon hints at
// the "add VIN from photo" affordance available on the detail page.
// Same surface treatment as OrphanOwnerBadge so the two flags share
// visual vocabulary; neutral because a missing VIN is a fact, not a
// system state (DESIGN.md §2).
function MissingVinBadge({ tooltip }: { tooltip: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-muted-foreground"
      title={tooltip}
    >
      <Camera className="size-3.5" aria-hidden="true" />
      <span className="sr-only">{tooltip}</span>
      <span aria-hidden="true" className="font-mono text-ink-3">
        —
      </span>
    </span>
  )
}

export function Vehicles() {
  const { t } = useTranslation('vehicles')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const { sort, dir, setSort, toggleDir } = useListSort('vehicles')

  const vehiclesQuery = useQuery({
    queryKey: ['vehicles', sort, dir],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const res = await fetch(`/api/vehicles?sort=${sort}&dir=${dir}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }

      return (await res.json()) as VehicleListRow[]
    },
  })

  const filtered = useMemo(() => {
    const data = vehiclesQuery.data ?? []
    const raw = search.trim()
    if (!raw) return data
    // phoneMatches kicks in whenever the query has any digit (area code,
    // partial number, full number) and surfaces every vehicle owned by
    // the matching customer — Emil's "unknown number is calling, what's
    // in their garage" flow. It returns false for letter-only input so
    // it's safe to OR alongside the existing make / model / VIN / plate
    // / owner-name substring filters.
    const q = raw.toLowerCase()
    return data.filter((v) => {
      const ymm = vehicleLabel(v).toLowerCase()
      return (
        ymm.includes(q) ||
        (v.vin ?? '').toLowerCase().includes(q) ||
        (v.plate ?? '').toLowerCase().includes(q) ||
        (v.owner_name ?? '').toLowerCase().includes(q) ||
        phoneMatches(raw, v.owner_phone)
      )
    })
  }, [vehiclesQuery.data, search])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
            {t('header.title')}
          </h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {t('header.subtitle', { count: vehiclesQuery.data?.length ?? 0 })}
          </p>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="search"
            placeholder={t('search.placeholder')}
            className="w-full sm:w-72"
          />
          <SortDropdown
            value={sort}
            dir={dir}
            onChange={(next) => {
              if (next === sort) return
              track('list.sort_changed', {
                entity: 'vehicles',
                from: sort,
                to: next,
              })
              setSort(next)
            }}
            onToggleDir={() => {
              track('list.sort_dir_toggled', {
                entity: 'vehicles',
                sort,
                to: dir === 'asc' ? 'desc' : 'asc',
              })
              toggleDir()
            }}
            namespace="vehicles"
            className="sm:w-48"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="size-4" />
            {t('add_manual')}
          </Button>
          <Button asChild size="sm">
            <Link to="/vehicles/from-photo">
              <Camera className="size-4" />
              {t('add_via_photo')}
            </Link>
          </Button>
        </div>
      </div>

      <VehicleDialog open={dialogOpen} onOpenChange={setDialogOpen} />

      {vehiclesQuery.isError ? (
        <div className="rounded-[14px] border border-destructive/20 bg-destructive-soft p-4 text-sm text-destructive">
          {vehiclesQuery.error instanceof Error
            ? vehiclesQuery.error.message
            : t('error')}
        </div>
      ) : null}

      {filtered.length === 0 && !vehiclesQuery.isLoading ? (
        <div className="rounded-[14px] border border-dashed border-border bg-background p-12 text-center text-sm text-muted-foreground">
          {t('empty')}
        </div>
      ) : (
        <>
          {/* Mobile / tablet — §4.8 list rows (below lg). Per CLAUDE.md the
              list-view-alternative pattern is preferred over horizontal
              scroll for primary list pages. While the query loads, the
              container shows skeleton rows (Activity.tsx idiom) instead
              of rendering as an empty bordered sliver. */}
          <div className="overflow-hidden rounded-[14px] border border-border bg-background lg:hidden">
            {vehiclesQuery.isLoading
              ? [0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex min-h-[60px] items-center gap-3 border-t border-border px-3.5 py-3 first:border-t-0"
                  >
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="h-3.5 w-2/5 animate-pulse rounded bg-muted/60 motion-reduce:animate-none" />
                      <div className="h-3 w-3/5 animate-pulse rounded bg-muted/40 motion-reduce:animate-none" />
                    </div>
                  </div>
                ))
              : filtered.map((v) => (
                  <div
                    key={v.id}
                    onClick={(e) => {
                      // Clicks that bubbled up from a real <a> inside the card
                      // (the owner link) shouldn't also trigger the row nav.
                      if ((e.target as HTMLElement).closest('a')) return
                      navigate(`/vehicles/${v.id}`)
                    }}
                    className="flex min-h-[60px] cursor-pointer items-center gap-3 border-t border-border px-3.5 py-3 transition-[background-color,transform] first:border-t-0 active:scale-[0.99] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
                  >
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/vehicles/${v.id}`}
                        className="block text-[15px] font-[620] text-foreground"
                      >
                        {vehicleLabel(v)}
                      </Link>
                      <EditableTagsReadOnly
                        tags={(v.tag_chips ?? []).slice(0, 3)}
                        size="xs"
                        className="mt-1"
                      />
                      <div className="mt-0.5 text-[13px] text-muted-foreground">
                        {engineLabel(v)}
                      </div>
                      {v.vin ? (
                        <div className="mt-2 tabular-nums">
                          <VinChip vin={v.vin} size="xs" />
                        </div>
                      ) : (
                        <div className="mt-2 text-xs">
                          <MissingVinBadge tooltip={t('card.no_vin_tooltip')} />
                        </div>
                      )}
                      <div className="mt-3 flex items-center justify-between gap-2 text-xs">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          {t('card.owner_label')}{' '}
                          {v.customer_id && v.owner_name ? (
                            <Link
                              to={`/customers/${v.customer_id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="font-medium text-foreground underline underline-offset-2"
                            >
                              {v.owner_name}
                            </Link>
                          ) : (
                            <OrphanOwnerBadge tooltip={t('card.no_owner_tooltip')} />
                          )}
                        </div>
                        <div className="font-mono font-semibold tabular-nums text-foreground">
                          {t('card.km_suffix', { value: formatMileage(v.mileage) })}
                        </div>
                      </div>
                    </div>
                    <ChevronRight
                      aria-hidden="true"
                      className="size-4 shrink-0 text-ink-3"
                    />
                  </div>
                ))}
          </div>

          {/* Desktop — full table (lg+). All columns visible; clicking the
              row navigates to /vehicles/:id, owner cell is its own Link
              with stopPropagation so it can route to /customers/:id. */}
          <div className="hidden lg:block overflow-hidden rounded-[14px] border border-border bg-background">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">{t('table.vehicle')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('table.engine')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('table.vin')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('table.owner')}</th>
                  <th className="px-4 py-3 text-right font-medium">{t('table.mileage_header')}</th>
                  <th className="px-2 py-3" aria-hidden="true" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((v) => (
                  <tr
                    key={v.id}
                    onClick={() => navigate(`/vehicles/${v.id}`)}
                    className="cursor-pointer transition-colors active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{vehicleLabel(v)}</div>
                      <div className="text-xs text-muted-foreground">{colorBodyLabel(v)}</div>
                      <EditableTagsReadOnly
                        tags={(v.tag_chips ?? []).slice(0, 3)}
                        size="xs"
                        className="mt-1"
                      />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{engineLabel(v)}</td>
                    <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                      {v.vin ? (
                        <VinChip vin={v.vin} size="xs" />
                      ) : (
                        <MissingVinBadge tooltip={t('card.no_vin_tooltip')} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {v.customer_id && v.owner_name ? (
                        <Link
                          to={`/customers/${v.customer_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-foreground underline underline-offset-2"
                        >
                          {v.owner_name}
                        </Link>
                      ) : (
                        <OrphanOwnerBadge tooltip={t('card.no_owner_tooltip')} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground">
                      {formatMileage(v.mileage)}
                    </td>
                    <td className="px-2 py-3 text-ink-3" aria-hidden="true">
                      ›
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
